import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Box,
  Button,
  CircularProgress,
  TableContainer,
  TableSortLabel,
  Avatar
} from "@mui/material";

import { windowsTableStyles } from "../styles/windowsTableStyles";
import axios from "../utils/axiosConfig";
import { formatDate } from "../utils/dateUtils";

const EmailsTable = ({ showDrafts }) => {
  const { t } = useTranslation(['common', 'buttons', 'forms', 'messages', 'pages']);
  const navigate = useNavigate();
  const { id } = useParams();
  const tableStyles = windowsTableStyles();

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  useEffect(() => {
    const fetchEmails = async () => {
      setLoading(true);
      try {
        // Updated endpoint to fetch emails for a specific case
        const res = await axios.get(`/api/email/${showDrafts ? 'drafts' : 'list'}/case/${id}`);
        let emailData = res.data || [];
        // Flatten emails so each recipient gets its own row (like EmailPage)
        let flattened = [];
        emailData.forEach(email => {
          const recipients = Array.isArray(email.recipient) ? email.recipient : [];
          const manualRecipients = Array.isArray(email.manualRecipient) ? email.manualRecipient : [];
          if (recipients.length === 0 && manualRecipients.length === 0) {
            // No recipients, still show the row
            flattened.push({ ...email, _rowRecipient: null });
          } else {
            recipients.forEach(rec => {
              flattened.push({ ...email, _rowRecipient: rec });
            });
            manualRecipients.forEach(m => {
              flattened.push({ ...email, _rowRecipient: { email: m, _id: 'manual' } });
            });
          }
        });
        setEmails(flattened);
      } catch (err) {
        console.error("Failed to fetch case emails:", err);
        setEmails([]);
      }
      setLoading(false);
    };
    fetchEmails();
  }, [showDrafts, id]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleOpenDraft = (draft) => {
    navigate(`/send-email?draftId=${draft._id}`);
  };

  const sortedEmails = [...emails].sort((a, b) => {
    if (!orderBy) return 0;
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    if (orderBy === "date" && a.createdAt && b.createdAt) {
      aValue = a.createdAt;
      bValue = b.createdAt;
    }
    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ backgroundColor: "white", padding: "32px 24px" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: "32px 24px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table sx={{ ...tableStyles.container, border: "1px solid #d1d1d1" }} size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>#</TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "from"}
                    direction={orderBy === "from" ? order : "asc"}
                    onClick={() => handleRequestSort("from")}
                  >
                    {t('forms:labels.from')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "recipient"}
                    direction={orderBy === "recipient" ? order : "asc"}
                    onClick={() => handleRequestSort("recipient")}
                  >
                    {t('forms:labels.recipients')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "date"}
                    direction={orderBy === "date" ? order : "asc"}
                    onClick={() => handleRequestSort("date")}
                  >
                    {t('forms:labels.date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('common:view_document')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  <TableSortLabel
                    active={orderBy === "subject"}
                    direction={orderBy === "subject" ? order : "asc"}
                    onClick={() => handleRequestSort("subject")}
                  >
                    {t('forms:labels.subject')}
                  </TableSortLabel>
                </TableCell>
                {showDrafts && (
                  <TableCell sx={tableStyles.headerCell}>{t('common:draft')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEmails
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((email, index) => (
                  <TableRow
                    sx={{
                      ...tableStyles.cell,
                      ...(email.isDraft && {
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          transition: 'background-color 0.2s ease'
                        }
                      })
                    }}
                    key={email._id}
                    onClick={() => {
                      if (email.isDraft) {
                        handleOpenDraft(email);
                      }
                    }}
                    style={{ cursor: email.isDraft ? "pointer" : "default" }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          src={email.from?.avatar}
                          alt={
                            email.from?.businessName
                              ? email.from.businessName
                              : email.from?.firstName || email.from?.email || ""
                          }
                          sx={{ width: "1rem", height: "1rem" }}
                        >
                          {email.from?.businessName
                            ? email.from.businessName[0]
                            : email.from?.firstName
                              ? email.from.firstName[0]
                              : (email.from?.email || "?")[0]}
                        </Avatar>
                        <span>
                          {email.from?.businessName
                            ? email.from.businessName
                            : email.from?.firstName
                              ? `${email.from.firstName} ${email.from.lastName || ""}`
                              : email.from?.email || email.from || ""}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>
                      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                        {email._rowRecipient ? (
                          <>

                          
                            <Avatar
                              src={email._rowRecipient.avatar}
                              alt={email._rowRecipient.businessName || email._rowRecipient.firstName || email._rowRecipient.email || email._rowRecipient || ""}
                              sx={{
                                width: "1rem",
                                height: "1rem",
                                bgcolor: email._rowRecipient._id === 'manual' ? '#757575' : undefined,
                                fontSize: email._rowRecipient._id === 'manual' ? '0.5rem' : '0.75rem'
                              }}
                            >
                              {email._rowRecipient.businessName
                                ? email._rowRecipient.businessName[0]
                                : email._rowRecipient.firstName
                                  ? email._rowRecipient.firstName[0]
                                  : (email._rowRecipient.email || email._rowRecipient || "?")[0]}
                            </Avatar>
                            <span>
                              {email._rowRecipient.businessName
                                ? email._rowRecipient.businessName
                                : email._rowRecipient.firstName
                                  ? `${email._rowRecipient.firstName} ${email._rowRecipient.lastName || ""}`
                                  : email._rowRecipient.email || email._rowRecipient || ""}
                            </span>
                          </>
                        ) : (
                          <span style={{ color: '#aaa' }}>-</span>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>
                      {email.createdAt ? formatDate(email.createdAt) : ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableStyles.cell,
                        color: "#005b92",
                        lineHeight: "12px",
                      }}
                    >
                      {email.documentName ? (
                        <span
                          style={{ color: "#005b92", textDecoration: "underline", cursor: "pointer" }}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const response = await axios.get(`/api/email/document/${email._id}`, {
                                responseType: "blob",
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement("a");
                              link.href = url;
                              link.setAttribute("download", email.documentName);
                              document.body.appendChild(link);
                              link.click();
                              link.parentNode.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (err) {
                              alert(t('messages:info.failed_to_download_document'));
                            }
                          }}
                        >
                          {email.documentName}
                        </span>
                      ) : (
                        <span style={{ color: "#222", fontWeight: 500, textDecoration: "none", cursor: "default" }}>{t('common:fallbacks.notAvailable')}</span>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell }}>{email.subject}</TableCell>
                    {showDrafts && (
                      <TableCell>
                        {email.isDraft ? t('common:draft') : ""}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={emails.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default EmailsTable; 